import { useCallback, useEffect, useState } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';
import { useI18n } from '../../context/useI18n';
import ManageGymOrgModal from '../admin/ManageGymOrgModal';
import { findOrgOperator } from '../../utils/organizationLicenseSummary';

/** Modal «Gestionar organización» reutilizable desde Inicio / Maestros (sin archivar). */
export default function GymOrgManageModal({
  scopeGymId = null,
  open = false,
  onClose,
  hideArchive = true,
  onManageLicenses = null,
  onOpenGymPanel = null,
  onSaved = null,
}) {
  const { t } = useI18n();
  const [gym, setGym] = useState(null);
  const [operator, setOperator] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');

  const load = useCallback(async () => {
    if (!scopeGymId || !open) return;
    setLoading(true);
    setLoadError('');
    try {
      const [gymRes, usersRes] = await Promise.all([
        api.get(`/gyms/${scopeGymId}`),
        api.get('/admin/users'),
      ]);
      setGym(gymRes.data || null);
      const users = usersRes.data?.data || [];
      setOperator(findOrgOperator(users, { gymId: scopeGymId }));
    } catch {
      setGym(null);
      setOperator(null);
      setLoadError(t('orgs.gym_load_error', 'No se pudo cargar la organización.'));
    } finally {
      setLoading(false);
    }
  }, [scopeGymId, open, t]);

  useEffect(() => {
    if (!open) {
      setGym(null);
      setOperator(null);
      setLoadError('');
      setLoading(false);
      return;
    }
    load();
  }, [open, load]);

  if (!open) return null;

  if (loading || loadError || !gym) {
    return (
      <div
        className="form-modal-overlay"
        style={{ zIndex: 9998 }}
        role="dialog"
        aria-modal="true"
        onClick={() => !loading && onClose?.()}
      >
        <div className="form-modal w-full max-w-md" onClick={(e) => e.stopPropagation()}>
          <div className="form-modal-header">
            <h3 className="font-bold">
              {t('orgs.manage_gym', 'Gestionar organización')}
            </h3>
            <button type="button" className="form-close-btn shrink-0" onClick={onClose} aria-label="Cerrar">
              <X size={18} />
            </button>
          </div>
          <div className="form-modal-content">
            {loading ? (
              <p className="text-stone-500">{t('common.loading', 'Cargando…')}</p>
            ) : (
              <p className="text-red-600 text-sm">{loadError}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <ManageGymOrgModal
      gym={gym}
      operator={operator}
      open
      onClose={onClose}
      onSaved={() => {
        load();
        onSaved?.();
      }}
      hideArchive={hideArchive}
      onManageLicenses={onManageLicenses}
      onOpenGymPanel={onOpenGymPanel}
    />
  );
}
